// Hero canvas — variant 4: liquid metal blob.
import React, { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment, MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";
import { useTheme } from "../../contexts/ThemeContext";

const h = React.createElement;

function Blob({ mouse, isDark }) {
  const ref = useRef();
  const matRef = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    ref.current.rotation.x = THREE.MathUtils.lerp(
      ref.current.rotation.x,
      mouse.current.y * 0.6 + Math.sin(t * 0.2) * 0.1,
      0.05,
    );
    ref.current.rotation.y = THREE.MathUtils.lerp(
      ref.current.rotation.y,
      mouse.current.x * 0.8 + t * 0.12,
      0.05,
    );
    // Pulse the distort amount
    if (matRef.current) {
      matRef.current.distort = 0.48 + Math.sin(t * 0.6) * 0.08;
    }
  });

  return h(
    Float,
    { speed: 1.4, rotationIntensity: 0.5, floatIntensity: 1.1 },
    h(
      "mesh",
      { ref, scale: 2.0 },
      h("icosahedronGeometry", { args: [1, 32] }),
      h(MeshDistortMaterial, {
        ref: matRef,
        color: isDark ? "#0c0c0c" : "#e4e4e7",
        roughness: 0.05,
        metalness: 1.0,
        clearcoat: 1,
        clearcoatRoughness: 0.05,
        distort: 0.5,
        speed: 1.8,
        envMapIntensity: 1.6,
      }),
    ),
  );
}

function Scene({ mouse, isDark }) {
  const bg = isDark ? "#050505" : "#fafafa";
  return h(
    React.Fragment,
    null,
    h("color", { attach: "background", args: [bg] }),
    h("fog", { attach: "fog", args: [bg, 6, 14] }),
    h("ambientLight", { intensity: isDark ? 0.15 : 0.45 }),
    // Coloured rim lights for chromatic feel
    h("pointLight", { position: [4, 2, 4], intensity: 1.6, color: isDark ? "#a78bfa" : "#7c3aed", distance: 12 }),
    h("pointLight", { position: [-4, -1, 3], intensity: 1.3, color: isDark ? "#22d3ee" : "#0ea5e9", distance: 12 }),
    h("pointLight", { position: [0, 3, -3], intensity: 0.9, color: "#ffffff", distance: 12 }),
    h(
      Suspense,
      { fallback: null },
      h(Blob, { mouse, isDark }),
      h(Environment, { preset: isDark ? "night" : "studio" }),
    ),
  );
}

export default function HeroCanvasLiquid() {
  const mouse = useRef({ x: 0, y: 0 });
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const onPointer = (e) => {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    mouse.current.x = x;
    mouse.current.y = y;
  };

  return (
    <div
      className="absolute inset-0 -z-0"
      onPointerMove={onPointer}
      data-testid="hero-canvas-liquid"
      aria-hidden="true"
    >
      <Canvas
        key={theme}
        dpr={[1, 1.6]}
        camera={{ position: [0, 0, 5.5], fov: 45 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <Scene mouse={mouse} isDark={isDark} />
      </Canvas>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(ellipse at center, transparent 35%, rgba(5,5,5,0.85) 88%)"
            : "radial-gradient(ellipse at center, transparent 35%, rgba(250,250,250,0.85) 88%)",
        }}
      />
    </div>
  );
}
